import { useId } from "react";
import { PsvMonitoringStateScope } from "@api/shared-runtime/psv-monitoring";
import type { PsvSourceFamily } from "@api/shared-runtime/psv-monitoring";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { USStateSelect } from "@/components/Selects/USStateSelect";
import {
  MONITORING_COPY,
  SOURCE_GROUPS,
  SOURCE_LABELS,
  STATE_SCOPE_HINTS,
} from "./monitoring-constants";
import { describeCoverageGap } from "./monitoring-format";
import {
  findSource,
  isStateScoped,
  replaceSource,
  toggleSource,
  withStateCodes,
  withStateScope,
} from "./monitoring-sources";
import type { SourceDraft } from "./monitoring-types";

type CoverageGap = { family: PsvSourceFamily; stateCodes: string[] };

type Props = {
  value: SourceDraft[];
  onChange: (sources: SourceDraft[]) => void;
  coverageGaps?: CoverageGap[];
  disabled?: boolean;
};

const STATE_SCOPE_OPTIONS = [
  { value: PsvMonitoringStateScope.All, label: "All states" },
  { value: PsvMonitoringStateScope.Selected, label: "Only these states" },
] as const;

/** Source checkboxes in the three design-doc groups; state-scoped families
 * reveal a scope choice and, for specific states, a required state picker. */
export function SourceSelector({
  value,
  onChange,
  coverageGaps = [],
  disabled = false,
}: Props) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {MONITORING_COPY.reverifiesOnly}
      </p>
      {SOURCE_GROUPS.map((group) => (
        <fieldset key={group.name} className="space-y-2">
          <legend className="text-sm font-medium">{group.name}</legend>
          {group.families.map((family) => (
            <SourceRow
              key={family}
              family={family}
              sources={value}
              onChange={onChange}
              gap={coverageGaps.find((gap) => gap.family === family)}
              disabled={disabled}
            />
          ))}
        </fieldset>
      ))}
    </div>
  );
}

function SourceRow({
  family,
  sources,
  onChange,
  gap,
  disabled,
}: {
  family: PsvSourceFamily;
  sources: SourceDraft[];
  onChange: (sources: SourceDraft[]) => void;
  gap?: CoverageGap;
  disabled: boolean;
}) {
  const checkboxId = useId();
  const source = findSource({ sources, family });
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Checkbox
          id={checkboxId}
          checked={!!source}
          disabled={disabled}
          onCheckedChange={(checked) =>
            onChange(
              toggleSource({ sources, family, selected: checked === true }),
            )
          }
        />
        <Label htmlFor={checkboxId} className="font-normal">
          {SOURCE_LABELS[family]}
        </Label>
      </div>
      {source && isStateScoped(family) && (
        <StateScopeFields
          source={source}
          disabled={disabled}
          gap={gap}
          onChange={(next) => onChange(replaceSource({ sources, next }))}
        />
      )}
    </div>
  );
}

function StateScopeFields({
  source,
  onChange,
  gap,
  disabled,
}: {
  source: SourceDraft;
  onChange: (source: SourceDraft) => void;
  gap?: CoverageGap;
  disabled: boolean;
}) {
  const hintId = useId();
  const errorId = useId();
  const radioName = useId();
  const selected = source.stateScope === PsvMonitoringStateScope.Selected;
  const missingStates = selected && source.stateCodes.length === 0;
  const hint =
    STATE_SCOPE_HINTS[source.family as keyof typeof STATE_SCOPE_HINTS];
  return (
    <div className="ml-6 space-y-2 border-l pl-4">
      <RadioGroup
        name={radioName}
        value={source.stateScope ?? undefined}
        disabled={disabled}
        onValueChange={(next) =>
          onChange(
            withStateScope({
              source,
              stateScope: next as PsvMonitoringStateScope,
            }),
          )
        }
        aria-describedby={hintId}
      >
        {STATE_SCOPE_OPTIONS.map((option) => {
          const optionId = `${radioName}-${option.value}`;
          return (
            <div key={option.value} className="flex items-center gap-2">
              <RadioGroupItem value={option.value} id={optionId} />
              <Label htmlFor={optionId} className="font-normal">
                {option.label}
              </Label>
            </div>
          );
        })}
      </RadioGroup>
      <p id={hintId} className="text-xs text-muted-foreground">
        {hint}
      </p>
      {selected && (
        <div className="space-y-1">
          <USStateSelect
            multiple
            value={source.stateCodes}
            onChange={(codes) =>
              onChange(
                withStateCodes({
                  source,
                  stateCodes: Array.isArray(codes) ? codes : [codes],
                }),
              )
            }
            placeholder="Choose states"
            aria-invalid={missingStates}
            aria-describedby={missingStates ? errorId : undefined}
          />
          {missingStates && (
            <p id={errorId} className="text-xs text-muted-foreground">
              {MONITORING_COPY.stateRequired}
            </p>
          )}
        </div>
      )}
      {gap && (
        <p className="text-xs text-amber-800">{describeCoverageGap(gap)}</p>
      )}
    </div>
  );
}
